
import React, {useContext, useEffect, useRef} from "react";
import classnames from "classnames";
import {
    Input,
    InputGroupAddon,
    InputGroupText,
    InputGroup,
    Row,
    Col,
    CardHeader, Card, CardBody, Button, FormGroup
} from "reactstrap";
import { UserLoginContext } from "../../context/UserLoginContext";
import {useHistory, useLocation} from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import NotificationAlert from "react-notification-alert";
import rest from "../../API/api";
import authService from "../../services/authService";

const EditUserForm = () => {
    const [userState, setUserState] = useContext(UserLoginContext);
    const location = useLocation();
    const userToEdit = location.state?.user;

    const [userId, setUserId] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [emailState, setEmailState] = React.useState("has-success");
    const [firstName, setFirstName] = React.useState("");
    const [firstNameState, setFirstNameState] = React.useState("has-success");
    const [lastName, setLastName] = React.useState("");
    const [lastNameState, setLastNameState] = React.useState("has-success");
    const [password, setPassword] = React.useState("");
    const [passwordState, setPasswordState] = React.useState("has-success");
    const [role, setRole] = React.useState("User");
    const [roleState, setRoleState] = React.useState("has-success");
    const [roleFocus, setRoleFocus] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [passwordFocus, setPasswordFocus] = React.useState(false);
    const [alert, setalert] = React.useState(null);
    const history = useHistory();
    const notificationAlertRef = useRef(null);

    useEffect(() => {
        if (userToEdit) {
            setUserId(userToEdit._id);
            setEmail(userToEdit.Email || userToEdit.Userid);
            setFirstName(userToEdit.Fname);
            setLastName(userToEdit.Lname);
            setRole(userToEdit.Role);
        } else {
            // If no user data, redirect back to users list
            history.push("/admin/users");
        }
    }, []);

    const stateFunctions = {
        setEmailState: (value) => setEmailState(value),
        setEmail: (value) => setEmail(value),
        setFirstNameState: (value) => setFirstNameState(value),
        setFirstName: (value) => setFirstName(value),
        setLastNameState: (value) => setLastNameState(value),
        setLastName: (value) => setLastName(value),
        setPasswordState: (value) => setPasswordState(value),
        setPassword: (value) => setPassword(value),
        setRoleState: (value) => setRoleState(value),
        setRole: (value) => setRole(value),
    };

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const verifyLength = (value, length) => {
        if (value.length >= length) {
            return true;
        }
        return false;
    };

    const verifyEmail = (value) => {
        var emailRex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (emailRex.test(value)) {
            return true;
        }
        return false;
    };

    const change = (event, stateName, type, stateNameEqualTo) => {
        switch (type) {
            case "length":
                if (verifyLength(event.target.value, stateNameEqualTo)) {
                    stateFunctions["set" + stateName + "State"]("has-success");
                } else {
                    stateFunctions["set" + stateName + "State"]("has-danger");
                }
                stateFunctions["set" + stateName](event.target.value);
                break;
            case "email":
                if (verifyEmail(event.target.value)) {
                    stateFunctions["set" + stateName + "State"]("has-success");
                } else {
                    stateFunctions["set" + stateName + "State"]("has-danger");
                }
                stateFunctions["set" + stateName](event.target.value);
                break;
            default:
                break;
        }
    };

    const isValidated = () => {
        // Password is optional for edit
        const passwordValid = password === "" || verifyLength(password, 6);

        if (
            emailState === "has-success" &&
            firstNameState === "has-success" &&
            lastNameState === "has-success" &&
            passwordValid &&
            roleState === "has-success"
        ) {
            return true;
        } else {
            if (emailState !== "has-success") {
                setEmailState("has-danger");
            }
            if (firstNameState !== "has-success") {
                setFirstNameState("has-danger");
            }
            if (lastNameState !== "has-success") {
                setLastNameState("has-danger");
            }
            if (!passwordValid) {
                setPasswordState("has-danger");
            }
            if (roleState !== "has-success") {
                setRoleState("has-danger");
            }
            return false;
        }
    };

    const _updateUserAlert = async () => {
        setalert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, update it!"
                confirmBtnBsStyle="info"
                cancelBtnBsStyle="default"
                title="Are you sure?"
                style={{display: "block"}}
                onConfirm={() => {
                    _update_user();
                    setalert(null);
                }}
                onCancel={() =>  {
                    setalert(null);
                }}
            >
                Are you sure you want to update this user?
            </SweetAlert>
        );
    };

    const _update_user = async () => {
        try {
            const updateData = {
                Email: email,
                Fname: firstName,
                Lname: lastName,
                Role: role
            };

            // Only include password if it was changed
            if (password && password.length > 0) {
                updateData.Password = password;
            }

            const response = await authService.authenticatedFetch(`${rest.users}/${userId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                notify("success", "User has been updated successfully!");
                setTimeout(() => {
                    history.push("/admin/users");
                }, 1000);
            } else {
                const errorData = await response.json();
                notify("danger", "Error updating user: " + (errorData.message || "Unknown error"));
            }
        } catch (error) {
            console.error('Error updating user:', error);
            notify("danger", "Error updating user: " + error.message);
        }
    };

    if (userState.status && userState.role === 'Admin') {
      return (
          <>
              <style>
                  {`
                      /* Hide validation feedback icons in InputGroups to prevent overlap with custom icons */
                      .input-group .form-control-feedback {
                          display: none !important;
                      }
                      .input-group.has-success:after,
                      .input-group.has-danger:after {
                          display: none !important;
                      }

                      /* Ensure InputGroups always have a visible border */
                      .input-group {
                          border: 1px solid #2b3553 !important;
                          border-radius: 0.4285rem !important;
                      }
                      .input-group:focus-within {
                          border-color: #1d8cf8 !important;
                      }
                      .input-group.has-danger {
                          border-color: #fd5d93 !important;
                      }
                      .input-group.has-success {
                          border-color: #00bf9a !important;
                      }
                      .input-group .form-control {
                          border: none !important;
                      }
                      .input-group .input-group-text {
                          border: none !important;
                          background-color: transparent !important;
                      }
                  `}
              </style>
              <div className="react-notification-alert-container">
                  <NotificationAlert ref={notificationAlertRef} />
              </div>
              <div className="content">
                  <Col md={8} className="ml-auto mr-auto">
                      <h2 className="text-center">Edit User</h2>
                  </Col>

                  <Card>
                      <CardHeader>User Information:</CardHeader>
                      <CardBody>
                          <Row>
                              <Col md={5} mdOffset={1}>
                                  <FormGroup className={`has-label ${emailState}`}>
                                      <label>Email *</label>
                                      <Input
                                          name="email"
                                          type="email"
                                          value={email}
                                          onChange={(e) => change(e, "Email", "email")}
                                      />
                                      {emailState === "has-danger" ? (
                                          <label className="error">
                                              Please enter a valid email address.
                                          </label>
                                      ) : null}
                                  </FormGroup>
                              </Col>
                              <Col md={5} mdOffset={1}>
                                  <FormGroup className="has-label">
                                      <label>Password (leave blank to keep current)</label>
                                      <InputGroup
                                          className={classnames(passwordState, {
                                              "input-group-focus": passwordFocus,
                                          })}
                                      >
                                          <Input
                                              name="password"
                                              type={showPassword ? "text" : "password"}
                                              value={password}
                                              placeholder="Enter new password to change"
                                              onChange={(e) => change(e, "Password", "length", 6)}
                                              onFocus={(e) => setPasswordFocus(true)}
                                              onBlur={(e) => setPasswordFocus(false)}
                                          />
                                          <InputGroupAddon addonType="append">
                                              <InputGroupText
                                                  style={{ cursor: 'pointer' }}
                                                  onClick={() => setShowPassword(!showPassword)}
                                              >
                                                  <i className={showPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                                              </InputGroupText>
                                          </InputGroupAddon>
                                      </InputGroup>
                                      {passwordState === "has-danger" ? (
                                          <label className="error">
                                              Password must be at least 6 characters if provided.
                                          </label>
                                      ) : null}
                                  </FormGroup>
                              </Col>
                          </Row>
                          <Row>
                              <Col md={5} mdOffset={1}>
                                  <FormGroup className={`has-label ${firstNameState}`}>
                                      <label>First Name *</label>
                                      <Input
                                          name="firstName"
                                          type="text"
                                          value={firstName}
                                          onChange={(e) => change(e, "FirstName", "length", 1)}
                                      />
                                      {firstNameState === "has-danger" ? (
                                          <label className="error">
                                              Please enter a first name.
                                          </label>
                                      ) : null}
                                  </FormGroup>
                              </Col>
                              <Col md={5} mdOffset={1}>
                                  <FormGroup className={`has-label ${lastNameState}`}>
                                      <label>Last Name *</label>
                                      <Input
                                          name="lastName"
                                          type="text"
                                          value={lastName}
                                          onChange={(e) => change(e, "LastName", "length", 1)}
                                      />
                                      {lastNameState === "has-danger" ? (
                                          <label className="error">
                                              Please enter a last name.
                                          </label>
                                      ) : null}
                                  </FormGroup>
                              </Col>
                          </Row>
                          <Row>
                              <Col md={5} mdOffset={1}>
                                  <InputGroup
                                      className={classnames(roleState, {
                                          "input-group-focus": roleFocus,
                                      })}
                                  >
                                      <InputGroupAddon addonType="prepend">
                                          <InputGroupText>
                                              Role * :
                                          </InputGroupText>
                                      </InputGroupAddon>

                                      <Input
                                          name="role"
                                          type="select"
                                          id="role"
                                          value={role}
                                          onChange={(e) => change(e, "Role", "length", 1)}
                                          onFocus={(e) => setRoleFocus(true)}
                                          onBlur={(e) => setRoleFocus(false)}
                                      >
                                          <option value="User">User</option>
                                          <option value="Admin">Admin</option>
                                      </Input>
                                      {roleState === "has-danger" ? (
                                          <label className="error">This field is required.</label>
                                      ) : null}
                                  </InputGroup>
                              </Col>
                          </Row>
                      </CardBody>
                  </Card>
                  <Card>
                  <CardBody>
                          <Row>
                              <Col md={1} mdOffset={1} xs={5} xsOffset={1}>
                                  <Button color="info" className="animation-on-hover" onClick={ async () => {
                                      if(isValidated()) {
                                          await _updateUserAlert();
                                      }
                                  }}>Update
                                  </Button>
                              </Col>
                              <Col md={1} mdOffset={1} xs={5}>
                                  <Button color="danger" className="animation-on-hover" onClick={ async () => {
                                          await history.push("/admin/users");
                                  }}>Cancel
                                  </Button>
                              </Col>
                          </Row>
                      </CardBody>
                  </Card>
                  {alert}
              </div>
          </>
      );
    }
    else {
        history.push("/admin/dashboard");
        return(null);
    }
};
export default EditUserForm;
